import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MasterLayout from '../../../masterLayout/MasterLayout';
import Breadcrumb from '../../../components/Breadcrumb';
import {
  AppButton,
  AppCard,
  AppEmptyState,
  AppFormField,
  AppInput,
  AppSelect,
  AppSpinner,
} from '../../../components/ui';
import secteursAdminService from '../../../services/secteursAdminService';
import secteursService from '../../../services/secteursService';
import { notify } from '../../../components/common/AppToast';
import { ROUTES } from '../../../config/routes';

const EMPTY = {
  nom: '', recordID: '', secteurHubspot: '', adresse: '', codePostal: '', ville: '',
  siret: '', latitude: '', longitude: '', zone_id: '',
};

const SecteursAdminEntrepriseFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const loadZones = secteursService.getZones().catch(() => []);

    const loadEntreprise = isEdit
      ? secteursAdminService.getEntreprise(id)
      : Promise.resolve(null);

    Promise.all([loadEntreprise, loadZones])
      .then(([entreprise, zonesList]) => {
        setZones(zonesList);
        if (entreprise) {
          setForm({
            nom: entreprise.nom || '',
            recordID: entreprise.recordID || '',
            secteurHubspot: entreprise.secteurHubspot || '',
            adresse: entreprise.adresse || '',
            codePostal: entreprise.codePostal || '',
            ville: entreprise.ville || '',
            siret: entreprise.siret || '',
            latitude: entreprise.latitude || '',
            longitude: entreprise.longitude || '',
            zone_id: entreprise.zone?.id ? String(entreprise.zone.id) : '',
          });
        }
      })
      .catch((err) => {
        setLoadError(err.message);
        notify.error(err.message);
      })
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      zone_id: form.zone_id ? Number(form.zone_id) : null,
      latitude: form.latitude || null,
      longitude: form.longitude || null,
    };
    try {
      if (isEdit) {
        await secteursAdminService.updateEntreprise(id, payload);
        notify.success('Entreprise enregistrée');
      } else {
        await secteursAdminService.createEntreprise(payload);
        notify.success('Entreprise créée');
      }
      navigate(ROUTES.secteurs.adminEntreprises);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MasterLayout>
        <AppSpinner />
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb
        title={isEdit ? 'Modifier l\'entreprise' : 'Nouvelle entreprise'}
        actions={(
          <AppButton as={Link} to={ROUTES.secteurs.adminEntreprises} size="sm" variant="outline-secondary">Retour</AppButton>
        )}
      />

      {loadError ? (
        <AppEmptyState message="Entreprise introuvable" />
      ) : (
        <AppCard>
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <AppFormField className="col-md-6" label="Nom" htmlFor="entreprise-nom" required>
                <AppInput
                  id="entreprise-nom"
                  type="text"
                  required
                  value={form.nom}
                  onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-6" label="Record ID HubSpot" htmlFor="entreprise-record" required>
                <AppInput
                  id="entreprise-record"
                  type="text"
                  required
                  value={form.recordID}
                  onChange={(e) => setForm((f) => ({ ...f, recordID: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-6" label="Secteur HubSpot" htmlFor="entreprise-secteur">
                <AppInput
                  id="entreprise-secteur"
                  type="text"
                  value={form.secteurHubspot}
                  onChange={(e) => setForm((f) => ({ ...f, secteurHubspot: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-6" label="SIRET" htmlFor="entreprise-siret">
                <AppInput
                  id="entreprise-siret"
                  type="text"
                  value={form.siret}
                  onChange={(e) => setForm((f) => ({ ...f, siret: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-12" label="Adresse" htmlFor="entreprise-adresse">
                <AppInput
                  id="entreprise-adresse"
                  type="text"
                  value={form.adresse}
                  onChange={(e) => setForm((f) => ({ ...f, adresse: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-4" label="Code postal" htmlFor="entreprise-cp">
                <AppInput
                  id="entreprise-cp"
                  type="text"
                  value={form.codePostal}
                  onChange={(e) => setForm((f) => ({ ...f, codePostal: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-4" label="Ville" htmlFor="entreprise-ville">
                <AppInput
                  id="entreprise-ville"
                  type="text"
                  value={form.ville}
                  onChange={(e) => setForm((f) => ({ ...f, ville: e.target.value }))}
                />
              </AppFormField>
              <AppFormField className="col-md-4" label="Zone" htmlFor="entreprise-zone">
                <AppSelect
                  id="entreprise-zone"
                  value={form.zone_id}
                  onChange={(e) => setForm((f) => ({ ...f, zone_id: e.target.value }))}
                >
                  <option value="">Aucune zone</option>
                  {zones.map((z) => (
                    <option key={z.id} value={String(z.id)}>{z.nomZone}</option>
                  ))}
                </AppSelect>
              </AppFormField>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-4">
              <AppButton as={Link} to={ROUTES.secteurs.adminEntreprises} variant="secondary">Annuler</AppButton>
              <AppButton type="submit" variant="success" loading={saving} loadingLabel="Enregistrement…">
                Enregistrer
              </AppButton>
            </div>
          </form>
        </AppCard>
      )}
    </MasterLayout>
  );
};

export default SecteursAdminEntrepriseFormPage;
